/**
 * Inventur mit Blindzählung (R38). Erste Anwendung: Erstzählung beim Umschalten von Billbee.
 * Lager: Fach-Etikett scannen → Artikel dieses Fachs zählen (ohne Sollbestand), Enter springt weiter,
 * fremde Artikel per Scan dazu, dann einreichen. Leitung: Differenzen ansehen und freigeben; die Differenzen
 * werden als Lagerbewegung „Inventur“ gebucht, bei der Umschaltung als „Startbestand Umschaltung“.
 */
import { useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { InventurFach, InventurKopf, InventurSicht } from '@storeos/contracts';
import * as inv from '../api/inventur';
import { datumZeit } from '../components/format';
import { ton } from '../components/ton';
import { Darf, Dialog, DialogFormular, Feld, Karte, Seitenkopf, Tabelle, useDarf, useToast, Zustand } from '../components/ui';

const STATUS_TEXT: Record<InventurKopf['status'], string> = { offen: 'Zählung läuft', eingereicht: 'Eingereicht, wartet auf Freigabe', freigegeben: 'Freigegeben, gebucht', verworfen: 'Verworfen' };
const ART_TEXT: Record<InventurKopf['art'], string> = { umschaltung: 'Startbestand Umschaltung (Billbee)', stichtag: 'Stichtagsinventur', gefuehrt_erst: 'Geführte Erstinventur (Tablet)' };

export function InventurSeite() {
  const qc = useQueryClient();
  const toast = useToast();
  const darf = useDarf();
  const liste = useQuery({ queryKey: ['inventuren'], queryFn: inv.inventuren });
  const laufend = liste.data?.find((i) => i.status === 'offen' || i.status === 'eingereicht') ?? null;
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);
  // Laufende Inventur, sonst die zuletzt gewählte bzw. jüngste (zeigt nach der Freigabe das Ergebnis).
  const id = gewaehlt ?? laufend?.id ?? liste.data?.[0]?.id ?? null;
  const sicht = useQuery({ queryKey: ['inventur', id], queryFn: () => inv.inventur(id!), enabled: Boolean(id) });
  const [starten, setStarten] = useState(false);
  const [bestaetigen, setBestaetigen] = useState(false);
  const neu = () => { void qc.invalidateQueries({ queryKey: ['inventuren'] }); void qc.invalidateQueries({ queryKey: ['inventur'] }); void qc.invalidateQueries({ queryKey: ['artikel'] }); };
  const k = sicht.data?.kopf;
  const sd = sicht.data;
  type Z = NonNullable<typeof sd>['zaehlungen'][number];

  async function aktion(f: () => Promise<unknown>, text: string) {
    try { await f(); toast(text); neu(); } catch (e) { toast((e as Error).message, 'fehler'); }
  }

  return (
    <section>
      <Seitenkopf titel="Inventur" unter="Blindzählung je Fach (Fach-Etikett scannen, Mengen eintippen) oder als geführte Erstinventur Artikel für Artikel am Tablet. Den Sollbestand sieht erst die Leitung nach dem Einreichen.">
        {!laufend && <Darf rolle="leitung"><button type="button" className="knopf" onClick={() => setStarten(true)} data-testid="inventur-starten">Inventur starten</button></Darf>}
      </Seitenkopf>
      <Zustand laden={liste.isLoading} fehler={liste.error}>
        {!laufend && <Karte><p className="hinweis">Keine laufende Inventur. {darf('leitung') ? 'Die Erstzählung beim Umschalten von Billbee startet hier.' : 'Die Leitung startet sie.'}</p></Karte>}
        {k && sd && (
          <div className={k.art === 'gefuehrt_erst' ? 'raster-stapel' : 'raster-2'}>
            <Karte titel={<>{k.titel} <span className="badge neutral" data-testid="inventur-status">{STATUS_TEXT[k.status]}</span></>}>
              <dl className="werte">
                <dt>Art</dt><dd>{ART_TEXT[k.art]}</dd>
                <dt>Gestartet</dt><dd>{datumZeit(k.created_at)}</dd>
                <dt>Zählungen</dt><dd data-testid="inventur-zaehlungen">{k.zaehlungen}</dd>
                {k.freigegeben_at && <><dt>Freigegeben</dt><dd>{datumZeit(k.freigegeben_at)} · {k.freigegeben_von ?? '–'}</dd></>}
                {k.differenzen !== null && <><dt>Differenzen</dt><dd>{k.differenzen} Position(en), Summe {k.differenz_summe! > 0 ? '+' : ''}{k.differenz_summe} Stück</dd></>}
              </dl>
              {k.status === 'offen' && k.art !== 'gefuehrt_erst' && <FachZaehlung inventurId={k.id} onGezaehlt={neu} />}
              {k.art === 'gefuehrt_erst' && <GefuehrtStand inventurId={k.id} offen={k.status === 'offen'} />}
              <div className="knopfreihe links">
                {k.status === 'offen' && k.art !== 'gefuehrt_erst' && <button type="button" className="knopf" disabled={!k.zaehlungen} onClick={() => aktion(() => inv.einreichen(k.id), 'Zählung eingereicht. Die Leitung prüft die Differenzen.')} data-testid="inventur-einreichen">Zählung einreichen</button>}
                {k.status === 'eingereicht' && k.art !== 'gefuehrt_erst' && <Darf rolle="leitung">
                  <button type="button" className="knopf" onClick={() => setBestaetigen(true)} data-testid="inventur-freigeben">Differenzen buchen (Freigabe)</button>
                  <button type="button" className="knopf-zweit" onClick={() => aktion(() => inv.nachzaehlen(k.id), 'Zur Nachzählung zurückgegeben')}>Nachzählen lassen</button>
                </Darf>}
                {(k.status === 'offen' || k.status === 'eingereicht') && <Darf rolle="leitung"><button type="button" className="knopf-gefahr" onClick={() => aktion(() => inv.verwerfen(k.id), 'Inventur verworfen, nichts gebucht')}>Verwerfen</button></Darf>}
              </div>
            </Karte>
            {k.art === 'gefuehrt_erst' ? <GefuehrtTabelle sicht={sd} onFertig={neu} /> : <Karte titel={sd.soll_sichtbar ? 'Zählung mit Soll und Differenz' : 'Gezählt (Blindzählung)'}>
              <Tabelle testid="inventur-tabelle" zeilen={sd.zaehlungen} schluessel={(z) => z.id} leer="Noch nichts gezählt"
                spalten={[
                  { kopf: 'Fach', zelle: (z) => z.bin_code, klasse: 'mono' },
                  { kopf: 'Artikel', zelle: (z) => <>{z.name}{z.variante ? <small className="hinweis"> · {z.variante}</small> : null}<br /><small className="mono hinweis">{z.sku}</small></> },
                  { kopf: 'Gezählt', zelle: (z) => z.gezaehlt, rechts: true },
                  ...(sd.soll_sichtbar ? [
                    { kopf: 'Soll', zelle: (z: Z) => z.soll ?? '–', rechts: true },
                    { kopf: 'Differenz', zelle: (z: Z) => <strong className={z.differenz ? (z.differenz > 0 ? 'ok-text' : 'warn-text') : ''}>{z.differenz! > 0 ? '+' : ''}{z.differenz ?? '–'}</strong>, rechts: true },
                  ] : []),
                ]} />
            </Karte>}
          </div>
        )}
        <Darf rolle="leitung"><AusgelaufenKarte /></Darf>
        {(liste.data?.length ?? 0) > 0 && (
          <Karte titel="Bisherige Inventuren">
            <Tabelle zeilen={liste.data!} schluessel={(i) => i.id} onZeile={(i) => setGewaehlt(i.id)} aktiv={id ?? undefined}
              spalten={[
                { kopf: 'Titel', zelle: (i) => i.titel },
                { kopf: 'Art', zelle: (i) => ART_TEXT[i.art] },
                { kopf: 'Status', zelle: (i) => STATUS_TEXT[i.status] },
                { kopf: 'Zählungen', zelle: (i) => i.zaehlungen, rechts: true },
                { kopf: 'Gestartet', zelle: (i) => datumZeit(i.created_at) },
              ]} />
          </Karte>
        )}
      </Zustand>
      <Dialog offen={starten} titel="Inventur starten" onSchliessen={() => setStarten(false)}>
        {starten && <StartFormular onAbbrechen={() => setStarten(false)} onFertig={(n) => { setStarten(false); setGewaehlt(n.id); toast('Inventur gestartet'); neu(); }} />}
      </Dialog>
      <Dialog offen={bestaetigen} titel="Differenzen buchen" onSchliessen={() => setBestaetigen(false)}>
        {bestaetigen && k && (
          <DialogFormular absendenText="Freigeben und buchen" onAbbrechen={() => setBestaetigen(false)} onAbsenden={async () => { await inv.freigeben(k.id); setBestaetigen(false); toast('Differenzen gebucht'); neu(); }}>
            <p>Je gezählter Position wird die Differenz zum Bestand am Fach als Lagerbewegung „Inventur“ gebucht{k.art === 'umschaltung' ? ', markiert als „Startbestand Umschaltung“' : ''}. Nicht gezählte Fächer bleiben unverändert. Das lässt sich nicht rückgängig machen, nur mit einer Korrekturbuchung.</p>
          </DialogFormular>
        )}
      </Dialog>
    </section>
  );
}

function StartFormular({ onAbbrechen, onFertig }: { onAbbrechen: () => void; onFertig: (k: InventurKopf) => void }) {
  const [art, setArt] = useState<InventurKopf['art']>('gefuehrt_erst');
  const [titel, setTitel] = useState('');
  return (
    <DialogFormular absendenText="Starten" onAbbrechen={onAbbrechen} onAbsenden={async () => onFertig(await inv.inventurStarten(art, titel.trim() || undefined))}>
      <Feld label="Art">
        <select value={art} onChange={(e) => setArt(e.target.value as InventurKopf['art'])} data-testid="inventur-art">
          <option value="gefuehrt_erst">Geführte Erstinventur: Artikel für Artikel am Tablet, über mehrere Tage</option>
          <option value="umschaltung">Erstzählung beim Umschalten von Billbee (Startbestand)</option>
          <option value="stichtag">Stichtagsinventur</option>
        </select>
      </Feld>
      <Feld label="Titel (optional)"><input value={titel} onChange={(e) => setTitel(e.target.value)} placeholder="z. B. Erstzählung Lager Karlsruhe" /></Feld>
    </DialogFormular>
  );
}

/** Fach scannen, dann Mengen der Artikel dieses Fachs eintippen. Kein Sollbestand sichtbar. */
function FachZaehlung({ inventurId, onGezaehlt }: { inventurId: string; onGezaehlt: () => void }) {
  const toast = useToast();
  const [code, setCode] = useState('');
  const [fach, setFach] = useState<InventurFach | null>(null);
  const [werte, setWerte] = useState<Record<string, string>>({});
  const [extra, setExtra] = useState('');
  const fachRef = useRef<HTMLInputElement>(null);
  const felder = useRef<Record<string, HTMLInputElement | null>>({});

  async function fachLaden(e?: FormEvent) {
    e?.preventDefault();
    if (!code.trim()) return;
    try {
      const f = await inv.inventurFach(inventurId, code.trim());
      setFach(f); setWerte(Object.fromEntries(f.artikel.map((a) => [a.article_id, a.gezaehlt === null ? '' : String(a.gezaehlt)])));
      ton('ok');
      setTimeout(() => felder.current[f.artikel[0]?.article_id ?? '']?.focus(), 0);
    } catch (err) { toast((err as Error).message, 'fehler'); ton('fehler'); }
  }

  async function speichere(articleId: string, naechster: string | null) {
    if (!fach) return;
    const w = werte[articleId];
    if (w === '' || w === undefined) { if (naechster) felder.current[naechster]?.focus(); return; }
    const n = Number(w);
    if (!Number.isInteger(n) || n < 0) { toast('Menge: ganze Zahl ab 0', 'fehler'); return; }
    try {
      await inv.zaehlen(inventurId, { bin_id: fach.bin_id, article_id: articleId, gezaehlt: n });
      ton('ok'); onGezaehlt();
      if (naechster) felder.current[naechster]?.focus();
      else { setFach(null); setCode(''); setTimeout(() => fachRef.current?.focus(), 0); toast(`Fach ${fach.bin_code} gezählt`); }
    } catch (err) { toast((err as Error).message, 'fehler'); ton('fehler'); }
  }

  async function artikelDazu(e: FormEvent) {
    e.preventDefault();
    if (!fach || !extra.trim()) return;
    try {
      await inv.zaehlen(inventurId, { bin_id: fach.bin_id, code: extra.trim(), gezaehlt: 0 });
      const f = await inv.inventurFach(inventurId, fach.bin_code);
      setFach(f); setWerte((alt) => ({ ...Object.fromEntries(f.artikel.map((a) => [a.article_id, a.gezaehlt === null ? '' : String(a.gezaehlt)])), ...alt }));
      setExtra('');
      const neuId = f.artikel.find((a) => !(a.article_id in werte))?.article_id;
      if (neuId) setTimeout(() => { felder.current[neuId]?.focus(); felder.current[neuId]?.select(); }, 0);
    } catch (err) { toast((err as Error).message, 'fehler'); ton('fehler'); }
  }

  return (
    <div className="inventur-zaehlung">
      {!fach ? (
        <form onSubmit={fachLaden} className="formular">
          <Feld label="Fach-Etikett scannen"><input ref={fachRef} autoFocus value={code} onChange={(e) => setCode(e.target.value)} placeholder="z. B. B-04-2" data-testid="inventur-fach" className="mono" /></Feld>
        </form>
      ) : (
        <>
          <h3>Fach <span className="mono" data-testid="inventur-fach-code">{fach.bin_code}</span></h3>
          {fach.artikel.length === 0 && <p className="hinweis">Keinem Artikel zugeordnet. Artikel unten scannen.</p>}
          <table className="tabelle inventur-liste">
            <thead><tr><th>Artikel</th><th className="rechts">Gezählt</th></tr></thead>
            <tbody>
              {fach.artikel.map((a, i) => (
                <tr key={a.article_id}>
                  <td>{a.name}{a.variante ? <small className="hinweis"> · {a.variante}</small> : null}<br /><small className="mono hinweis">{a.sku}</small></td>
                  <td className="rechts">
                    <input ref={(el) => { felder.current[a.article_id] = el; }} value={werte[a.article_id] ?? ''} inputMode="numeric" className="menge-feld" data-testid="inventur-menge" aria-label={`Menge ${a.sku}`}
                      onChange={(e) => setWerte({ ...werte, [a.article_id]: e.target.value })}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void speichere(a.article_id, fach.artikel[i + 1]?.article_id ?? null); } }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <form onSubmit={artikelDazu} className="formular zeile">
            <Feld label="Anderer Artikel im Fach (SKU/EAN scannen)"><input value={extra} onChange={(e) => setExtra(e.target.value)} data-testid="inventur-artikel-scan" /></Feld>
          </form>
          <div className="knopfreihe links">
            <button type="button" className="knopf-zweit" onClick={() => { setFach(null); setCode(''); setTimeout(() => fachRef.current?.focus(), 0); }}>Nächstes Fach</button>
          </div>
          <p className="hinweis">Enter speichert die Menge und springt zum nächsten Artikel; nach dem letzten geht es zum nächsten Fach. Leere Felder werden nicht gezählt.</p>
        </>
      )}
    </div>
  );
}

/** Geführte Erstinventur (R126): Fortschritt und Weg zum Tablet. */
function GefuehrtStand({ inventurId, offen }: { inventurId: string; offen: boolean }) {
  const w = useQuery({ queryKey: ['weiter', inventurId, null], queryFn: () => inv.weiter(inventurId) });
  const f = w.data?.fortschritt;
  return (
    <div className="inventur-gefuehrt">
      {f && (
        <dl className="werte" data-testid="inventur-fortschritt">
          <dt>Gezählt</dt><dd><strong>{f.gezaehlt}</strong> von {f.gesamt} Artikeln</dd>
          <dt>Heute</dt><dd>{f.heute}</dd>
          <dt>Später</dt><dd>{f.spaeter}{f.nachzaehlen ? ` · Nachzählen ${f.nachzaehlen}` : ''}</dd>
          {f.warenwert_anteil !== null && <><dt>Warenwert</dt><dd>{Math.round(f.warenwert_anteil * 100)} % gezählt</dd></>}
        </dl>
      )}
      {offen && <Link to="/inventur/weiter" className="knopf gross" data-testid="inventur-weiter">Weiterzählen</Link>}
      {offen && <p className="hinweis">Ein Artikel pro Bildschirm, die teuersten zuerst. Jede Eingabe ist sofort gespeichert; Einreichen geht, wenn alles gezählt ist.</p>}
    </div>
  );
}

/** Leitung: Zählung mit Verkäufen seit Zählung, Ergebnis, bisherigem Wert und Differenz; Nachzählen einzelner Artikel; Freigabe. */
function GefuehrtTabelle({ sicht, onFertig }: { sicht: InventurSicht; onFertig: () => void }) {
  const toast = useToast();
  const k = sicht.kopf;
  const [wahl, setWahl] = useState<Set<string>>(new Set());
  const [gruende, setGruende] = useState<Record<string, string>>({});
  const [frei, setFrei] = useState(false);
  type Z = InventurSicht['zaehlungen'][number];
  const pruefen = sicht.soll_sichtbar && k.status === 'eingereicht';
  const unplausibel = sicht.zaehlungen.filter((z) => z.warnung);
  // Beim Prüfen zuerst die unplausiblen, dann die größten Differenzen
  const zeilen = sicht.soll_sichtbar
    ? [...sicht.zaehlungen].sort((a, b) => Number(Boolean(b.warnung)) - Number(Boolean(a.warnung)) || Math.abs(b.differenz ?? 0) - Math.abs(a.differenz ?? 0))
    : sicht.zaehlungen;
  const umschalten = (id: string) => setWahl((w) => { const n = new Set(w); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const spalten = [
    ...(pruefen ? [{ kopf: '', zelle: (z: Z) => <input type="checkbox" aria-label={`Nachzählen ${z.sku}`} checked={wahl.has(z.article_id)} onChange={() => umschalten(z.article_id)} /> }] : []),
    { kopf: 'Artikel', zelle: (z: Z) => <>{z.name}{z.variante ? <small className="hinweis"> · {z.variante}</small> : null}<br /><small className="mono hinweis">{z.sku}</small>{z.kategorie ? <small className="hinweis"> · {z.kategorie}</small> : null}{z.warnung ? <><br /><small className="warn-text" title={z.warnung}>unplausibel: gezählt {z.gezaehlt}, seitdem verkauft {z.verkauft_seit}</small></> : null}</> },
    { kopf: 'Platz', zelle: (z: Z) => z.bin_code ?? '–', klasse: 'mono' },
    { kopf: 'Gezählt', zelle: (z: Z) => <>{z.gezaehlt}{z.ausgelaufen ? <> <span className="badge warn">ausgelaufen</span></> : null}{z.nachzaehlen ? <> <span className="badge neutral">nachzählen</span></> : null}</>, rechts: true },
    ...(sicht.soll_sichtbar ? [
      { kopf: 'Verkauft seit Zählung', zelle: (z: Z) => z.verkauft_seit ?? '–', rechts: true },
      { kopf: 'Ergebnis', zelle: (z: Z) => <strong>{z.ergebnis ?? '–'}</strong>, rechts: true },
      { kopf: 'Bisher', zelle: (z: Z) => <>{z.bisher ?? '–'}{z.bestand_fuehren === false ? <small className="hinweis"> (ohne Bestandsführung)</small> : null}</>, rechts: true },
      { kopf: 'Differenz', zelle: (z: Z) => <strong className={z.differenz ? (z.differenz > 0 ? 'ok-text' : 'warn-text') : ''}>{z.differenz! > 0 ? '+' : ''}{z.differenz ?? '–'}</strong>, rechts: true },
    ] : []),
  ];
  return (
    <Karte titel={sicht.soll_sichtbar ? 'Prüfen: gezählt, verkauft seit Zählung, Ergebnis' : 'Gezählt (Blindzählung)'}>
      {pruefen && unplausibel.length > 0 && (
        <div className="ps-meldung fehler" data-testid="inventur-unplausibel">
          {unplausibel.length} Artikel unplausibel: seit der Zählung mehr verkauft als gezählt – Wareneingang dazwischen oder Zählfehler? Nachzählen lassen oder bei der Freigabe bewusst mit 0 buchen (mit Grund).
        </div>
      )}
      <Tabelle testid="inventur-tabelle" zeilen={zeilen} schluessel={(z) => z.id} leer="Noch nichts gezählt" spalten={spalten} />
      {pruefen && (
        <div className="knopfreihe links">
          <button type="button" className="knopf" onClick={() => setFrei(true)} data-testid="inventur-freigeben">Freigeben und buchen</button>
          <button type="button" className="knopf-zweit" disabled={!wahl.size} data-testid="inventur-nachzaehlen-auswahl"
            onClick={async () => { try { await inv.nachzaehlen(k.id, [...wahl]); toast(`${wahl.size} Artikel zum Nachzählen zurückgegeben`); setWahl(new Set()); onFertig(); } catch (e) { toast((e as Error).message, 'fehler'); } }}>
            Auswahl nachzählen lassen ({wahl.size})
          </button>
        </div>
      )}
      <Dialog offen={frei} titel="Erstinventur freigeben" onSchliessen={() => setFrei(false)} breit>
        {frei && (
          <DialogFormular absendenText="Freigeben und buchen" onAbbrechen={() => setFrei(false)}
            onAbsenden={async () => {
              await inv.freigeben(k.id, unplausibel.map((z) => ({ article_id: z.article_id, grund: gruende[z.article_id] ?? '' })));
              setFrei(false); toast('Erstinventur gebucht'); onFertig();
            }}>
            <p>Je Artikel wird das Ergebnis (gezählt − verkauft seit Zählung) als Bestand am Zählplatz gebucht und ersetzt den Startbestand aus Woo. Ausgelaufene Artikel ohne Rest verlieren die Bestandsführung. Danach gilt die Erstzählung der Umschaltung als erledigt (Runbook 4.15 wird Stichprobe).</p>
            {unplausibel.map((z) => (
              <Feld key={z.id} label={`${z.sku} mit 0 freigeben – Grund`} hinweis={z.warnung ?? undefined}>
                <input value={gruende[z.article_id] ?? ''} onChange={(e) => setGruende({ ...gruende, [z.article_id]: e.target.value })} data-testid="inventur-null-grund" />
              </Feld>
            ))}
          </DialogFormular>
        )}
      </Dialog>
    </Karte>
  );
}

/** Leitung: ausgelaufene Artikel (keine Änderung in Woo), CSV für Thomas, Rückgängig mit Grund. */
function AusgelaufenKarte() {
  const toast = useToast();
  const qc = useQueryClient();
  const l = useQuery({ queryKey: ['ausgelaufen'], queryFn: inv.ausgelaufen });
  const [zurueck, setZurueck] = useState<{ id: string; sku: string } | null>(null);
  const [grund, setGrund] = useState('');
  if (!l.data?.length) return null;
  return (
    <Karte titel={`Ausgelaufen (${l.data.length})`} aktion={<a className="knopf-zweit klein" href={inv.AUSGELAUFEN_CSV} download data-testid="ausgelaufen-csv">CSV</a>}>
      <p className="hinweis">In Woo ändert StoreOS nichts. Die Liste ist für die gesammelte Entscheidung im Shop.</p>
      <Tabelle testid="ausgelaufen-tabelle" zeilen={l.data} schluessel={(a) => a.article_id}
        spalten={[
          { kopf: 'SKU', zelle: (a) => a.sku, klasse: 'mono' },
          { kopf: 'Artikel', zelle: (a) => <>{a.name}{a.variante ? <small className="hinweis"> · {a.variante}</small> : null}</> },
          { kopf: 'Woo-ID', zelle: (a) => a.woo_id ?? '–', klasse: 'mono' },
          { kopf: 'Restmenge', zelle: (a) => a.restmenge, rechts: true },
          { kopf: 'Bestand', zelle: (a) => a.physisch, rechts: true },
          { kopf: 'Seit', zelle: (a) => <>{datumZeit(a.ausgelaufen_at)}{a.ausgelaufen_von ? <small className="hinweis"> · {a.ausgelaufen_von}</small> : null}</> },
          { kopf: '', zelle: (a) => <button type="button" className="knopf-flach" onClick={() => { setZurueck({ id: a.article_id, sku: a.sku }); setGrund(''); }}>Rückgängig</button> },
        ]} />
      <Dialog offen={zurueck !== null} titel={`${zurueck?.sku ?? ''} wieder im Sortiment`} onSchliessen={() => setZurueck(null)}>
        {zurueck && (
          <DialogFormular absendenText="Rückgängig machen" onAbbrechen={() => setZurueck(null)}
            onAbsenden={async () => { await inv.ausgelaufenRueckgaengig(zurueck.id, grund); setZurueck(null); toast('Wieder im Sortiment'); void qc.invalidateQueries({ queryKey: ['ausgelaufen'] }); }}>
            <Feld label="Grund"><input autoFocus value={grund} onChange={(e) => setGrund(e.target.value)} placeholder="z. B. wird doch nachgedruckt" /></Feld>
          </DialogFormular>
        )}
      </Dialog>
    </Karte>
  );
}
